import {uniqueId, sortBy, every, keyBy, find, flatten, range, last, mapValues} from 'lodash';
import fetchJSON from '../../../utils/fetchJSON';
import turfLineDistance from '@turf/line-distance';
import turfLineSlice from '@turf/line-slice';
import getLineLocalId from '../../../utils/gaia/getLineLocalId';
import {boldTrackColors} from '../../../utils/gaia/trackColors';
import {registerSavedTrack} from '../../../utils/gaia/saveTrack';
import {registerSavedPoint} from '../../../utils/gaia/savePoint';

export default function takeLocal(
    action: TakeLocalTrackAction,
    tracks: {[trackLocalId: string]: Track},
    setTracks: React.Dispatch<React.SetStateAction<{[trackLocalId: string]: Track}>>,
    highlight: MapHighlight,
) {
    const {track} = action;
    const trackLocalId = (track.properties.id || track.properties.trackLocalId)!;

    if (tracks[trackLocalId]) {
        if (action.callback) action.callback(tracks[trackLocalId]);
        return trackLocalId;
    }

    const initialTrack: Track = {
        ...action.track,
        isGeometryLoaded: false,
        properties: {
            trackLocalId,
            isLocallyDeleted: false,
            routing_mode: 'pedestrian',
            color: boldTrackColors[0],
            title: '',
            notes: '',
            isLocallyCreated: false,

            // We encode "null" as a string in vector tiles
            ...mapValues(track.properties, value =>
                value === 'null' ? null : value
            ) as Track['properties'],
        }
    };

    registerSavedTrack(initialTrack);

    if (!tracks[trackLocalId]) {
        setTracks({
            ...tracks,
            [trackLocalId]: initialTrack
        });
    }

    const pubLink: string = (
        highlight &&
        highlight.type === 'track' &&
        highlight.pubLink &&
        highlight.id === track.properties.id
    ) ? `?pubLink=${highlight.pubLink}` : '';

    fetchJSON(`/api/objects/track/${track.properties.id}${pubLink}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: {'Content-Type': 'application/json'},
    }).then(collection => {

        const unmodifiedMultiline: RemoteTrack = find(
            collection.features,
            (feature: any) => feature.geometry.type === 'MultiLineString'
        );

        const unmodifiedLineGeometry: GeoJSON.LineString = {
            type: 'LineString',
            coordinates: flatten(unmodifiedMultiline.geometry.coordinates),
        };

        const unmodifiedLine: TrackLine = {
            type: 'Feature',
            properties: {
                trackLocalId,
                fromPointLocalId: undefined,
                toPointLocalId: undefined,
                type: 'normal',
                lineLocalId: uniqueId('line'),
                isGeometryLoaded: true,
            },
            geometry: unmodifiedLineGeometry
        };

        const unmodifiedPoints = track.properties.isPublicTrack ? [] : collection.features
            .filter((feature: RemoteTrack | RemoteTrackPoint) =>
                feature.geometry.type === 'Point'
            );

        const hasOrderProperty = every(unmodifiedPoints, (point: RemoteTrackPoint) =>
            point.properties.order !== null && point.properties.order !== undefined
        );

        const points: TrackPoint[] = sortBy(unmodifiedPoints, (point: RemoteTrackPoint) => {
            if (hasOrderProperty) {
                return point.properties.order;
            } else {
                return getLinePosition(unmodifiedLine, point);
            }
        }).map((remotePoint: RemoteTrackPoint, order: number): TrackPoint => {
            const point = {
                ...remotePoint,
                properties: {
                    ...remotePoint.properties,
                    trackLocalId,
                    pointLocalId: remotePoint.properties.id || uniqueId('point'),
                    isLocallyDeleted: false,
                    isGeometryModified: false,
                    isTitleLoaded: true,
                    isLocallyCreated: false,
                    order,
                }
            };
            registerSavedPoint(point);
            return point;
        });

        if (initialTrack.properties.track_type !== 'route') {
            setTracks(latestTracks => {
                const latestTrack: Track = {
                    properties: {
                        trackLocalId,
                        ...initialTrack.properties,
                        ...collection.features[0]!.properties,
                    },
                    points: keyBy(points, 'properties.pointLocalId'),
                    unmodifiedLine,
                    isGeometryLoaded: true,
                };
                registerSavedTrack(latestTrack);
                if (action.callback) action.callback(latestTrack);
                return {
                    ...latestTracks,
                    [trackLocalId]: latestTrack
                };
            });

        } else {

            const unmodifiedLines: TrackLine[] = range(points.length - 1).map(index => {
                const fromPoint = points[index];
                const toPoint = points[index + 1];
                const lineLocalId = getLineLocalId(trackLocalId, fromPoint, toPoint);

                return {
                    type: 'Feature',
                    properties: {
                        trackLocalId,
                        fromPointLocalId: fromPoint && fromPoint.properties.pointLocalId,
                        toPointLocalId: toPoint && toPoint.properties.pointLocalId,
                        type: 'normal',
                        lineLocalId,
                        isGeometryLoaded: true,
                    },
                    geometry: turfLineSlice(
                        fromPoint || unmodifiedLine.geometry.coordinates[0],
                        toPoint || last(unmodifiedLine.geometry.coordinates),
                        unmodifiedLine
                    ).geometry!,
                };
            });

            const latestTrack: Track = {
                properties: {
                    trackLocalId,
                    ...initialTrack.properties,
                    ...collection.features[0]!.properties,
                    routing_mode: collection.features[0]!.properties.routing_mode || 'pedestrian',
                    track_type: points.length ? initialTrack.properties.track_type : 'track',
                },
                isGeometryLoaded: true,
                points: keyBy(points, 'properties.pointLocalId'),
                unmodifiedLines: keyBy(unmodifiedLines, 'properties.lineLocalId'),
                unmodifiedLine,
            };

            registerSavedTrack(latestTrack);
            if (action.callback) action.callback(latestTrack);

            setTracks(latestTracks => ({
                ...latestTracks,
                [trackLocalId]: latestTrack
            }));
        }
    });

    return trackLocalId;
}

// We do not store the index of each track point. We guess at the sort order using the geometry.
// https://github.com/trailbehind/GaiaCloud/blob/master/gaia/static/js/app/util/TBGeoUtil.js#L4-L13
function getLinePosition(line: TrackLine, point: RemoteTrackPoint) {
    const totalDistance = turfLineDistance(line);
    const lineBeforePoint = turfLineSlice({
        coordinates: line.geometry.coordinates[0],
        type: 'Point'
    }, point, line);
    const distanceBeforePoint = turfLineDistance(lineBeforePoint);
    return distanceBeforePoint / totalDistance;
}
